import React from "react";
import styled from "styled-components";

import type { TargetMetric } from "types/BusinessDashboard";
import { formatNumberToWonCurrency } from "utils/format";

interface TargetDataCardProps {
  type: "bep" | "target";
  data: TargetMetric;
  onEditClick: () => void;
}

const CardWrapper = styled.div<{ $gray?: boolean }>`
  border-radius: 12px;
  height: 88px;
  display: flex;
  flex-direction: column;
  border: 1px solid #e3e7ef;
  overflow: hidden;
  flex: 1;
  background: ${({ $gray }) => ($gray ? "#F4F5 F6" : "#fff")};
`;

const Top = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 52px;
  padding-left: 10px;
  padding-right: 10px;
`;

const Title = styled.span`
  color: #383c42;
  font-size: 12px;
  font-weight: 600;
  line-height: 14px;
  letter-spacing: -0.2px;
`;

const TopRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TargetAmount = styled.span`
  color: #383c42;
  font-size: 12px;
  font-weight: 600;
  line-height: 14px;
  letter-spacing: -0.2px;
`;

const EditSmallButton = styled.button`
  border-radius: 6px;
  border: 1px solid #d9dee8b2;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  width: 39px;
  background: #fff;
`;

const EditSmallButtonText = styled.span`
  color: #4c5874;
  font-weight: 500;
  font-size: 11px;
  line-height: 14px;
  letter-spacing: -0.2px;
`;

const Bottom = styled.div<{ $bgColor: string }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 52px;
  padding-left: 10px;
  padding-right: 10px;
  background: ${({ $bgColor }) => $bgColor};
`;

const BottomLeft = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const BottomLabel = styled.span`
  color: #fff;
  font-weight: 600;
  font-size: 13px;
  line-height: 18px;
  letter-spacing: -0.2px;
`;

const BottomAmount = styled.span`
  color: #fff;
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: -0.2px;
`;

const BottomRight = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Achievement = styled.p<{ $bgColor: string }>`
  color: ${({ $bgColor }) => $bgColor};
  font-weight: 700;
  font-size: 13px;
  line-height: 18px;
  letter-spacing: -0.2px;
  background-color: white;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  padding: 0 6px;
`;

export const TargetDataCard = ({
  type,
  data,
  onEditClick,
}: TargetDataCardProps) => {
  // --- 목표 금액이 설정된 경우 ---
  const config = {
    bep: {
      title: "BEP (손익분기점)",
      achieved: {
        bgColor: "#1F6AFF",
        label: "BEP 초과 금액",
      },
      notAchieved: {
        bgColor: "#FD685D",
        label: "BEP까지 남은 금액",
      },
    },
    target: {
      title: "목표 매출",
      achieved: {
        bgColor: "#1F6AFF",
        label: "목표 초과 달성",
      },
      notAchieved: {
        bgColor: "#FD685D",
        label: "목표 매출까지 남은 금액",
      },
    },
  };

  if (!data || data?.targetAmount <= 0) {
    const promptText =
      type === "bep"
        ? "BEP 입력하고, 달성 현황을 확인해 보세요!"
        : "목표 매출을 입력하고, 달성률을 확인해 보세요!";

    return (
      <CardWrapper>
        <Top>
          <Title>{config[type].title}</Title>
          <TopRight>
            <TargetAmount>₩0</TargetAmount>
          </TopRight>
        </Top>
        <Bottom
          $bgColor="#9FA2A9"
          style={{ justifyContent: "center", gap: "6px" }}
          onClick={onEditClick}
        >
          <BottomLabel>{promptText}</BottomLabel>
          <svg
            width="7"
            height="12"
            viewBox="0 0 7 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0.5 0.969727L5.5 5.96997L0.5 10.9697"
              stroke="white"
              strokeLinecap="round"
            />
          </svg>
        </Bottom>
      </CardWrapper>
    );
  }

  const { targetAmount, isAchieved, achievementRate, differenceFromTarget } =
    data;

  // --- 목표 금액이 설정되지 않았을 경우 (0원 이하) ---
  if (targetAmount <= 0) {
    const promptText =
      type === "bep"
        ? "BEP 입력하고, 달성 현황을 확인해 보세요!"
        : "목표 매출을 입력하고, 달성률을 확인해 보세요!";

    return (
      <CardWrapper>
        <Top>
          <Title>{config[type].title}</Title>
          <TopRight>
            <TargetAmount>₩0</TargetAmount>
          </TopRight>
        </Top>
        <Bottom
          $bgColor="#9FA2A9"
          style={{ justifyContent: "center", gap: "6px" }}
          onClick={onEditClick}
        >
          <BottomLabel>{promptText}</BottomLabel>
          <svg
            width="7"
            height="12"
            viewBox="0 0 7 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0.5 0.969727L5.5 5.96997L0.5 10.9697"
              stroke="white"
              strokeLinecap="round"
            />
          </svg>
        </Bottom>
      </CardWrapper>
    );
  }

  const currentConfig = isAchieved
    ? config[type].achieved
    : config[type].notAchieved;

  return (
    <CardWrapper>
      <Top>
        <Title>{config[type].title}</Title>
        <TopRight>
          <TargetAmount>{formatNumberToWonCurrency(targetAmount)}</TargetAmount>
          <EditSmallButton onClick={onEditClick}>
            <EditSmallButtonText>수정</EditSmallButtonText>
          </EditSmallButton>
        </TopRight>
      </Top>
      <Bottom $bgColor={currentConfig.bgColor}>
        <BottomLeft>
          <BottomLabel>{currentConfig.label}</BottomLabel>
        </BottomLeft>
        <BottomRight>
          <BottomAmount>
            {formatNumberToWonCurrency(differenceFromTarget)}
          </BottomAmount>
          <Achievement $bgColor={currentConfig.bgColor}>
            {achievementRate >= 0 ? "+" : ""}
            {achievementRate.toFixed(1)}%
          </Achievement>
        </BottomRight>
      </Bottom>
    </CardWrapper>
  );
};
